// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import MiniCart from "@hardwoods/App/Components/MiniCart/MiniCart";
import { TippyPopupStyle } from "@hardwoods/App/Components/Tippy/Interface";
import TippyPopup from "@hardwoods/App/Components/Tippy/TippyPopup";
import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getCommon } from "@hardwoods/App/Store/Selectors/CommonState";
import HeaderPopupLink from "@hardwoods/Overrides/Widgets/Common/HeaderPopupLink";
import ShoppingCart from "@hardwoods/XDL/Icons/ShoppingCart";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart:
        !getCurrentCartState(state).value &&
        !getCurrentCartState(state).isLoading &&
        getCurrentPage(state).type !== "CartPage",
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    isMobile: getCommon(state).isMobile,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;

    tippyPopup?: TippyPopupStyle;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;


const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    isMobile,
    orderSubTotalDisplay,
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
}) => {
    const history = useHistory();
    const [isPopupVisible, setPopupVisibility] = useState(false);

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    // XNG-Changes
    const onPopupVisibilityUpdate = (popupVisibility: boolean) => {
        setPopupVisibility(popupVisibility);
    };

    const tippyOptions: { [key: string]: any } = {
        arrow: false,
        offset: [0, 0],
        placement: "bottom-end",
        className: "no-padding",
        enableMobileMode: true,
        updateTriggerOptionsOnMobile: true,
        withCloseButtonOnMobile: true,
        onPopupVisibilityUpdate,
    };

    return (
        <TippyPopup
            extendedStyles={styles.tippyPopup}
            content={<MiniCart isShown={isPopupVisible} />}
            {...tippyOptions}
        >
            <HeaderPopupLink
                showIcon={showIcon}
                icon={icon}
                isOpened={isPopupVisible}
                onClick={() => !isMobile && history.push(cartUrl ?? "")}
                iconProps={{ type: "miniCart" }}
                clickableProps={{
                    "data-test-selector": "cartLink",
                }}
                titleProps={{
                    isLoading: !orderSubTotalDisplay,
                    "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
                }}
            >
                {retainedTotalCountToDisplay && retainedTotalCountToDisplay > 0 ? (
                    <>
                        {`${retainedTotalCountToDisplay} ${translate(" Items")}`}
                        <Typography className="sub-title">{orderSubTotalDisplay}</Typography>
                    </>
                ) : (
                    <>{orderSubTotalDisplay ? translate("My Cart") : ""}</>
                )}
            </HeaderPopupLink>
        </TippyPopup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
